import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Users, MapPin, ShoppingBag, Calculator, CornerDownLeft } from "lucide-react";
import { api } from "@/lib/api";
import { TOOL_LIST } from "@/pages/ToolsIndex";

const PAGES = [
  { label: "Plan Builder — custom training block", path: "/plan" },
  { label: "Ranked — best run clubs in America", path: "/ranked" },
  { label: "Find a Club — national directory", path: "/clubs" },
  { label: "Training Hub — guides and articles", path: "/training" },
  { label: "Tools — 21 free calculators", path: "/tools" },
  { label: "Shop — the collection", path: "/shop" },
  { label: "Events", path: "/events" },
  { label: "Stories", path: "/stories" },
  { label: "Collabs", path: "/collabs" },
  { label: "Join the Club", path: "/join" },
];

const ICONS = { article: FileText, club: Users, city: MapPin, product: ShoppingBag, tool: Calculator, page: CornerDownLeft };

export const SearchCommand = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState({ articles: [], clubs: [], cities: [], products: [] });
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setRemote({ articles: [], clubs: [], cities: [], products: [] });
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setRemote({ articles: [], clubs: [], cities: [], products: [] });
      return;
    }
    timer.current = setTimeout(() => {
      api.get("/search", { params: { q: q.trim() } }).then((r) => setRemote(r.data)).catch(() => {});
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const items = [];
    if (needle.length >= 2) {
      remote.clubs.forEach((c) => items.push({ type: "club", label: c.name, sub: `${c.city}, ${c.state}`, path: `/clubs/${c.slug}` }));
      remote.cities.forEach((c) => items.push({ type: "city", label: `${c.name}, ${c.state}`, sub: `${c.club_count || ""} clubs · city guide`, path: `/run-clubs/${c.state_slug}/${c.slug}` }));
      remote.articles.forEach((a) => items.push({ type: "article", label: a.title, sub: `${a.category} · ${a.read_time} min read`, path: `/training/${a.slug}` }));
      TOOL_LIST.filter(([, name, desc]) => `${name} ${desc}`.toLowerCase().includes(needle))
        .slice(0, 5)
        .forEach(([slug, name, desc]) => items.push({ type: "tool", label: name, sub: desc, path: `/tools/${slug}` }));
      remote.products.forEach((p) => items.push({ type: "product", label: p.name, sub: p.price ? `$${p.price} · shop` : "shop", path: "/shop" }));
      PAGES.filter((p) => p.label.toLowerCase().includes(needle)).slice(0, 3)
        .forEach((p) => items.push({ type: "page", label: p.label, sub: "page", path: p.path }));
    } else {
      PAGES.slice(0, 6).forEach((p) => items.push({ type: "page", label: p.label, sub: "page", path: p.path }));
    }
    return items.slice(0, 18);
  }, [q, remote]);

  const go = useCallback((item) => {
    onClose();
    navigate(item.path);
  }, [navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) { e.preventDefault(); go(results[active]); }
    else if (e.key === "Escape") onClose();
  };

  useEffect(() => setActive(0), [results.length, q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="search-overlay">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mx-auto mt-[10vh] w-[92vw] max-w-2xl border border-white/20 bg-ink shadow-2xl" data-testid="search-dialog">
        <div className="flex items-center gap-3 border-b border-white/10 px-5">
          <Search size={17} className="text-white/40 shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search clubs, cities, articles, tools, merch..."
            className="w-full bg-transparent py-4 text-white placeholder:text-white/30 text-base"
          />
          <kbd className="font-mono-label text-white/30 border border-white/15 px-2 py-1 shrink-0 hidden sm:block">ESC</kbd>
        </div>
        <div className="max-h-[55vh] overflow-y-auto py-2" data-testid="search-results">
          {results.length === 0 && q.trim().length >= 2 && (
            <p className="font-mono-label text-white/40 px-5 py-8 text-center" data-testid="search-empty">NOTHING FOUND. TRY A CITY, A DISTANCE, OR A CLUB NAME.</p>
          )}
          {results.map((r, i) => {
            const Icon = ICONS[r.type];
            return (
              <button
                key={`${r.path}-${i}`}
                data-testid={`search-result-${i}`}
                onClick={() => go(r)}
                onMouseEnter={() => setActive(i)}
                className={`w-full flex items-center gap-4 px-5 py-3 text-left transition-colors duration-100 ${i === active ? "bg-white text-ink" : "text-white/80"}`}
              >
                <Icon size={15} className={i === active ? "text-ink/60" : "text-white/35"} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm truncate">{r.label}</span>
                  <span className={`block text-xs truncate ${i === active ? "text-ink/50" : "text-white/35"}`}>{r.sub}</span>
                </span>
                <span className={`font-mono-label shrink-0 ${i === active ? "text-ink/40" : "text-white/25"}`}>{r.type.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
